import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { buildWaLink } from "@/lib/constants";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1663006163468-f653c4b14e59?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const FOUNDER_IMG =
  "https://images.unsplash.com/photo-1744197068961-18194526c236?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function About() {
  useReveal();

  return (
    <div data-testid="about-page" className="pt-28 md:pt-32">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start reveal">
          <div className="md:col-span-7">
            <p className="eyebrow mb-5">About iExplore</p>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-[#1F3A5F] leading-[1.05]">
              Explore Beyond <em className="not-italic text-[#1F3A5F]/60">Limits.</em>
            </h1>
            <p className="mt-7 text-[#5A6B82] leading-relaxed text-base sm:text-lg max-w-xl">
              At iExplore, we believe travel is not just about visiting places —
              it's about experiencing them deeply, meaningfully, and without
              stress. We specialise in Ladakh — crafting journeys that go
              beyond typical itineraries into real landscapes, real culture,
              and real adventure.
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden bg-[#11233B]">
              <img
                src={ABOUT_IMG}
                alt="Ladakh landscape"
                className="w-full h-full object-cover kenburns"
              />
            </div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20 reveal">
          <div className="md:col-span-5">
            <p className="eyebrow mb-3">The Story</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-[#1F3A5F] leading-[1.05]">
              Built through years on the road —<br />
              <em className="not-italic text-[#1F3A5F]/60">not in a boardroom.</em>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-6 text-[#5A6B82] leading-relaxed text-base">
            <p>
              Our founder, <strong className="text-[#11233B]">Rohan Trivedi</strong>, has spent over a
              decade working in tourism — handling everything from travel
              operations to customised itineraries for travellers across India.
            </p>
            <p>
              From managing inbound travel for NRIs to specialising in Ladakh
              packages, the journey has always been the same: understanding
              what travellers truly want, solving real on-ground challenges,
              and delivering smooth, memorable experiences.
            </p>
            <p>
              After years of working behind the scenes, iExplore was created
              with a simple goal — to offer{" "}
              <em className="text-[#11233B]">honest, well-planned, and unforgettable</em> Ladakh
              journeys, without confusion, overpricing, or generic packages.
            </p>
          </div>
        </div>
      </section>

      {/* WHY LADAKH */}
      <section className="bg-[#11233B] text-[#FAF4ED]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20 reveal">
            <div className="md:col-span-5">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={FOUNDER_IMG}
                  alt="Ladakh monastery"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="md:col-span-7">
              <p className="eyebrow mb-3 text-[#E8B89B]">Why Ladakh</p>
              <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.05]">
                Ladakh is not a place to "just visit."
              </h2>
              <div className="mt-8 space-y-5 text-[#FAF4ED]/80 leading-relaxed text-base">
                <p>
                  It's a place that demands proper planning, acclimatisation,
                  and local understanding. From Pangong Lake to Nubra Valley,
                  from Khardung La to Umling La — every route, every day, every
                  altitude matters.
                </p>
                <p>And that's where we come in.</p>
              </div>

              <ul className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#FAF4ED]/15">
                {[
                  "Curated itineraries — never copy-paste",
                  "Real on-ground understanding",
                  "Budget to premium flexibility",
                  "Personal support before & during your trip",
                  "Transparent planning, no hidden confusion",
                  "A specialised travel partner — not a mass operator",
                ].map((line, i) => (
                  <li
                    key={line}
                    className="bg-[#11233B] p-5 text-sm text-[#FAF4ED]/90"
                  >
                    <span className="font-serif text-[#E8B89B] mr-3">
                      0{i + 1}
                    </span>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 reveal">
        <div className="bg-white border border-[#1F3A5F]/10 p-10 md:p-20 text-center">
          <h2 className="font-serif font-light text-4xl md:text-5xl text-[#1F3A5F] leading-[1.05] max-w-3xl mx-auto">
            Whether it's your first Ladakh trip or a once-in-a-lifetime
            adventure — we'll help you do it right.
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 justify-center">
            <a
              href={buildWaLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-whatsapp"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bf5a] text-white px-10 py-5 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              Chat on WhatsApp
            </a>
            <Link
              to="/packages"
              data-testid="about-view-packages"
              className="inline-flex items-center justify-center gap-2 bg-[#1F3A5F] hover:bg-[#11233B] text-[#FAF4ED] px-10 py-5 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              View All Packages <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
